import React, { useEffect, useState } from "react";
import ChartDrawingCoordinates from "./ChartDrawingCoordinateEditor";
import {
  drawingCoordinateFields,
  drawingPriceSummary,
} from "./chartDrawingCoordinates";

export const CHART_DRAWING_ROLE_OPTIONS = [
  ["support", "Support"],
  ["resistance", "Resistance"],
  ["entry", "Entry"],
  ["confirmation", "Confirmation"],
  ["stop_loss", "Invalidation"],
  ["price_target", "Target"],
  ["trend", "Trend"],
  ["evidence", "Evidence"],
  ["note", "Note"],
];

export const CHART_DRAWING_COLOR_OPTIONS = [
  ["#60a5fa", "Blue"],
  ["#2dd4bf", "Teal"],
  ["#fbbf24", "Amber"],
  ["#fb7185", "Rose"],
  ["#c084fc", "Purple"],
  ["#e2e8f0", "Slate"],
];

const titleize = (value) =>
  String(value || "Drawing")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
export const drawingRolePatch = (overlay, role) => {
  const oldRole = overlay.semantic_role || overlay.semanticRole || "note";
  const oldLabel = CHART_DRAWING_ROLE_OPTIONS.find(
    ([value]) => value === oldRole,
  )?.[1];
  const nextLabel = CHART_DRAWING_ROLE_OPTIONS.find(
    ([value]) => value === role,
  )?.[1];
  return {
    semantic_role: role,
    ...([oldLabel, titleize(oldRole)].includes(overlay.label)
      ? { label: nextLabel || titleize(role) }
      : {}),
  };
};

export default function ChartDrawingInspector({
  busy = false,
  contextLabel = null,
  dark = false,
  onAsk,
  onChange,
  onClose,
  onCopy,
  onDelete,
  onHide,
  onMonitor,
  onUseInPlan,
  onReviewPlan,
  overlay,
  palette,
  planContext = null,
  tickSize = null,
  initialTab = "style",
}) {
  const [label, setLabel] = useState(overlay?.label || "");
  const [tab, setTab] = useState(initialTab);
  const [localAction, setLocalAction] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setLabel(overlay?.label || "");
    setError(null);
  }, [overlay?.id, overlay?.label]);
  const locked = overlay?.locked === true;
  const role = overlay?.semantic_role || overlay?.semanticRole || "note";
  const confirmed =
    overlay?.user_confirmed === true ||
    String(overlay?.id || "").startsWith("manual:") ||
    overlay?.source === "workspace_direct";
  const disabled = busy || Boolean(localAction);
  const button = {
    minHeight: 36,
    padding: "0 10px",
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    background: "transparent",
    color: palette.text,
    cursor: "pointer",
    font: "inherit",
    fontSize: 12,
  };
  const input = {
    ...button,
    width: "100%",
    boxSizing: "border-box",
    height: 36,
    background: dark ? "#181d26" : "#fff",
  };
  const labelStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    color: palette.mutedText,
    fontSize: 11,
  };
  const freehand = ["brush", "highlighter"].includes(overlay?.kind);
  const widths = freehand ? [2, 4, 8, 14] : [1, 2, 3, 4];
  const strokeWidth =
    Number(overlay?.stroke_width ?? overlay?.strokeWidth) || 2;
  const coordinates = drawingCoordinateFields(overlay).length > 0;
  const run = async (name, callback) => {
    if (disabled || typeof callback !== "function") return false;
    setLocalAction(name);
    setError(null);
    try {
      const result = await callback();
      if (result === false || result?.ok === false) {
        setError(result?.error || "This change could not be saved. Try again.");
        return false;
      }
      return result ?? true;
    } catch (actionError) {
      setError(actionError?.message || "This change could not be saved.");
      return false;
    } finally {
      setLocalAction(null);
    }
  };
  const commit = (patch) => run("save", () => onChange?.(patch));
  const tabs = [
    ["style", "Style"],
    ...(coordinates ? [["coordinates", "Coordinates"]] : []),
    ...(planContext?.visible ? [["plan", "Plan"]] : []),
  ];
  const activeTab = tabs.some(([key]) => key === tab) ? tab : "style";

  return (
    <div
      data-chart-drawing-inspector
      data-chart-drawing-inspector-role={role}
      data-chart-drawing-inspector-locked={locked ? "true" : "false"}
      data-chart-drawing-plan-status={planContext?.status}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (
          event.key === "Escape" &&
          !event.target.closest("input, textarea, select")
        )
          onClose();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div>
          <div
            data-chart-drawing-provenance
            style={{
              fontSize: 11,
              color: confirmed ? "#0d9488" : "#3b82f6",
              marginBottom: 4,
            }}
          >
            {confirmed ? "User confirmed" : "Incite proposal"}
            {locked ? " · Locked" : ""}
          </div>
          <div style={{ fontSize: 15, fontWeight: 750, color: palette.text }}>
            Drawing details
          </div>
          <div style={{ fontSize: 11, color: palette.mutedText, marginTop: 4 }}>
            {[titleize(overlay?.kind), contextLabel]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        <button
          type="button"
          data-chart-drawing-inspector-close
          aria-label="Close drawing details"
          onClick={onClose}
          style={{ ...button, border: 0, padding: "0 8px", fontSize: 20 }}
        >
          ×
        </button>
      </div>
      <div
        role="tablist"
        aria-label="Drawing details"
        style={{
          display: "flex",
          gap: 4,
          borderBottom: `1px solid ${palette.border}`,
          paddingBottom: 8,
        }}
      >
        {tabs.map(([key, text]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            data-chart-drawing-tab={key}
            onClick={() => setTab(key)}
            style={{
              ...button,
              flex: 1,
              padding: "0 6px",
              borderColor: activeTab === key ? "#60a5fa" : "transparent",
              background:
                activeTab === key ? "rgba(96,165,250,0.1)" : "transparent",
            }}
          >
            {text}
          </button>
        ))}
      </div>
      {activeTab === "style" ? (
        <div
          role="tabpanel"
          aria-label="Drawing style"
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const next = label.trim();
              if (next) void commit({ label: next.slice(0, 120) });
            }}
          >
            <label style={labelStyle}>
              Label
              <input
                data-chart-drawing-label-input
                aria-label="Drawing label"
                value={label}
                disabled={disabled || locked}
                maxLength={120}
                onChange={(event) => setLabel(event.target.value)}
                onBlur={() => {
                  if (label.trim() && label.trim() !== overlay.label)
                    void commit({ label: label.trim() });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setLabel(overlay.label || "");
                }}
                style={input}
              />
            </label>
          </form>
          <label style={labelStyle}>
            Role
            <select
              data-chart-drawing-role-select
              aria-label="Drawing role"
              value={role}
              disabled={disabled || locked}
              onChange={(event) =>
                void commit(drawingRolePatch(overlay, event.target.value))
              }
              style={input}
            >
              {CHART_DRAWING_ROLE_OPTIONS.map(([value, text]) => (
                <option value={value} key={value}>
                  {text}
                </option>
              ))}
            </select>
          </label>
          <div style={labelStyle}>
            Width
            <div
              data-chart-drawing-width-options
              style={{ display: "flex", gap: 6 }}
            >
              {widths.map((width) => (
                <button
                  key={width}
                  type="button"
                  aria-label={`Drawing width ${width}`}
                  aria-pressed={Math.round(strokeWidth) === width}
                  disabled={disabled || locked}
                  onClick={() => void commit({ stroke_width: width })}
                  style={{
                    ...button,
                    flex: 1,
                    borderColor:
                      Math.round(strokeWidth) === width
                        ? overlay.color || "#60a5fa"
                        : palette.border,
                  }}
                >
                  {width}
                </button>
              ))}
            </div>
          </div>
          <div style={labelStyle}>
            Color
            <div
              data-chart-drawing-color-options
              style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
            >
              {CHART_DRAWING_COLOR_OPTIONS.map(([color, text]) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Drawing color ${text}`}
                  aria-pressed={overlay.color === color}
                  disabled={disabled || locked}
                  onClick={() => void commit({ color })}
                  style={{
                    width: 36,
                    height: 36,
                    padding: 5,
                    borderRadius: "50%",
                    border: `2px solid ${overlay.color === color ? palette.text : "transparent"}`,
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      background: color,
                      borderRadius: "50%",
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {activeTab === "coordinates" ? (
        <div role="tabpanel" aria-label="Drawing coordinates">
          <ChartDrawingCoordinates
            overlay={overlay}
            tickSize={tickSize}
            onChange={onChange}
            palette={palette}
            busy={disabled}
          />
        </div>
      ) : null}
      {activeTab === "plan" ? (
        <div
          role="tabpanel"
          aria-label="Drawing plan"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <strong style={{ fontSize: 13, color: palette.text }}>
            {planContext.planTitle || "Use in a plan"}
          </strong>
          {planContext.linkedCondition ? (
            <div
              data-chart-drawing-plan-comparison
              style={{
                padding: 10,
                borderRadius: 8,
                background: "rgba(96,165,250,0.08)",
                fontSize: 12,
                lineHeight: 1.8,
                color: palette.text,
              }}
            >
              <div>
                Drawing: <strong>{drawingPriceSummary(overlay)}</strong>
              </div>
              <div>
                Saved plan:{" "}
                <strong>
                  {drawingPriceSummary(planContext.linkedCondition.definition)}
                </strong>
              </div>
            </div>
          ) : null}
          <div
            data-chart-drawing-plan-hint
            style={{ fontSize: 12, color: palette.mutedText, lineHeight: 1.5 }}
          >
            {planContext.hint}
          </div>
          {planContext.canUseInPlan ? (
            <button
              type="button"
              data-chart-drawing-use-in-plan
              disabled={disabled}
              onClick={() => void run("plan", onUseInPlan)}
              style={{ ...button, color: "#2563eb", borderColor: "#60a5fa" }}
            >
              {localAction === "plan" ? "Saving…" : planContext.useInPlanLabel}
            </button>
          ) : null}
          {typeof onReviewPlan === "function" ? (
            <button
              type="button"
              data-chart-drawing-review-plan
              disabled={disabled}
              onClick={() => void run("review", onReviewPlan)}
              style={button}
            >
              Review plan
            </button>
          ) : null}
          {planContext.canMonitor ? (
            <button
              type="button"
              data-chart-drawing-create-monitor
              disabled={disabled}
              onClick={() => void run("monitor", onMonitor)}
              style={button}
            >
              {localAction === "monitor"
                ? "Opening…"
                : planContext.monitorLabel}
            </button>
          ) : (
            <div
              style={{
                fontSize: 11,
                lineHeight: 1.5,
                color: palette.mutedText,
              }}
            >
              {planContext.status?.startsWith("confirmed")
                ? "The plan and any existing monitors keep their confirmed values."
                : "Confirm the plan and match its saved level before setting up monitoring."}
            </div>
          )}
        </div>
      ) : null}
      {error ? (
        <div
          data-chart-drawing-inspector-error
          role="alert"
          style={{ color: "#ef4444", fontSize: 12 }}
        >
          {error}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          paddingTop: 10,
          borderTop: `1px solid ${palette.border}`,
        }}
      >
        {onAsk ? (
          <button
            type="button"
            data-chart-drawing-ask-incite
            disabled={disabled}
            onClick={() => void run("ask", onAsk)}
            style={{ ...button, width: "100%", color: "#2563eb" }}
          >
            Ask Incite
          </button>
        ) : null}
        <button
          type="button"
          data-chart-drawing-lock
          aria-pressed={locked}
          disabled={disabled}
          onClick={() => void commit({ locked: !locked })}
          style={button}
        >
          {locked ? "Unlock" : "Lock"}
        </button>
        {onCopy ? (
          <button
            type="button"
            data-chart-drawing-copy
            disabled={disabled}
            onClick={() => void run("copy", onCopy)}
            style={button}
          >
            Copy
          </button>
        ) : null}
        <button
          type="button"
          data-chart-drawing-hide
          disabled={disabled}
          onClick={() => void run("hide", onHide)}
          style={button}
        >
          Hide
        </button>
        <button
          type="button"
          data-chart-drawing-delete
          disabled={disabled || locked}
          title={locked ? "Unlock before deleting" : undefined}
          onClick={() => void run("delete", onDelete)}
          style={{ ...button, color: "#ef4444" }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}
